import React, { useState } from "react";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * DemoTourModal — Loom-style scripted step-by-step walkthrough rendered from
 * a JSON step list. Used by Feature Explorer for modules that don't yet have
 * a recorded video walkthrough.
 *
 * Props:
 *   module       { icon, t, c, ...tile } — the module object from MODULES
 *   steps        [{ title, body, image?, cta? }]
 *   onClose()
 *   onOpen()     — called when the final "Open module" button is hit
 */
const TONE_ACCENTS = {
  cyan: "text-cyan-300",
  fuchsia: "text-fuchsia-300",
  emerald: "text-emerald-300",
  amber: "text-amber-300",
};

const TONE_BG = {
  cyan: "from-cyan-400 to-blue-500",
  fuchsia: "from-fuchsia-400 to-pink-500",
  emerald: "from-emerald-400 to-teal-500",
  amber: "from-amber-400 to-orange-500",
};

export function DemoTourModal({ module: m, steps, onClose, onOpen }) {
  const [idx, setIdx] = useState(0);
  const total = steps.length;
  const step = steps[idx];
  const isLast = idx === total - 1;
  const accent = TONE_ACCENTS[m.c] || TONE_ACCENTS.cyan;
  const bg = TONE_BG[m.c] || TONE_BG.cyan;

  const next = () => {
    if (isLast) {
      trackEvent("feature_walkthrough_complete", { module: m.t });
      onOpen();
      return;
    }
    setIdx(idx + 1);
  };
  const prev = () => setIdx(Math.max(0, idx - 1));

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      data-testid="demo-tour-modal"
      onClick={onClose}
    >
      <div
        className="bg-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className={`text-[10px] uppercase tracking-wider ${accent}`}>Guided tour</div>
            <h3 className="font-display text-lg font-bold text-white mt-0.5 flex items-center gap-2">
              <m.icon className={`w-4 h-4 ${accent}`} /> {m.t}
            </h3>
          </div>
          <button
            onClick={onClose}
            data-testid="demo-tour-close"
            className="text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-6 pt-5">
          <div className="flex gap-1.5" data-testid="demo-tour-progress">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= idx ? `bg-gradient-to-r ${bg}` : "bg-white/10"
                }`}
              />
            ))}
          </div>
          <div className="mt-2 text-[10px] uppercase tracking-wider text-slate-500">
            Step {idx + 1} of {total}
          </div>
        </div>

        <div className="px-6 py-8 min-h-[220px]">
          <h4
            className="font-display text-2xl font-bold text-white"
            data-testid="demo-tour-step-title"
          >
            {step.title}
          </h4>
          <p
            className="mt-3 text-slate-300 leading-relaxed"
            data-testid="demo-tour-step-body"
          >
            {step.body}
          </p>
          {step.image && (
            <img
              src={step.image}
              alt={step.title}
              className="mt-5 w-full rounded-lg border border-white/10"
            />
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/5 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={idx === 0}
            data-testid="demo-tour-prev-btn"
            className="text-sm text-slate-400 hover:text-white px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={next}
            data-testid="demo-tour-next-btn"
            className={`text-sm px-4 py-2 rounded-lg bg-gradient-to-r ${bg} text-slate-900 font-semibold inline-flex items-center gap-1.5`}
          >
            {isLast ? (
              <>
                {step.cta || "Open module"} <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Next <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
